import React from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { EFilingParty, EFilingAttorney } from '@lds-efsp/shared-contracts';

// Plaintiff-side roleCodes — mirrors PLAINTIFF_ROLES in
// packages/shared-contracts/src/features/efiling/efm/jti/pipeline/stage-3-envelope.ts.
// Kept as a local copy so the UI doesn't import the JTI pipeline.
const PLAINTIFF_ROLE_CODES: ReadonlySet<string> = new Set([
  'PLAIN',
  'PET',
  'CLAIM',
  'XCMPLNT',
  'XPLAIN',
  'INTPLAIN',
  'INTVEN',
  'INTPLD',
]);

const PLAINTIFF_AVATAR_COLOR = '#3A6BCE';
const DEFENDANT_AVATAR_COLOR = '#94A3B8';
const ATTORNEY_AVATAR_COLOR = '#D97706';

// eslint-disable-next-line react-refresh/only-export-components
export function isPlaintiffSideRole(roleCode: string): boolean {
  return PLAINTIFF_ROLE_CODES.has(roleCode);
}

interface AvatarProps {
  letter: string;
  color: string;
  ariaLabel: string;
}

const Avatar: React.FC<AvatarProps> = ({ letter, color, ariaLabel }) => (
  <div
    aria-label={ariaLabel}
    data-testid={`avatar-${ariaLabel}`}
    className="flex items-center justify-center rounded-full text-white font-semibold flex-shrink-0"
    style={{ backgroundColor: color, width: 40, height: 40, fontSize: 18 }}
  >
    {letter}
  </div>
);

interface PartyCardProps {
  brand: BrandConfig;
  party: EFilingParty;
  displayName: string;
  onEdit: () => void;
  onDelete: () => void;
}

export const PartyCard: React.FC<PartyCardProps> = ({
  brand,
  party,
  displayName,
  onEdit,
  onDelete,
}) => {
  const isPlaintiff = isPlaintiffSideRole(party.roleCode);
  const avatarLetter = isPlaintiff ? 'P' : 'D';
  const avatarColor = isPlaintiff ? PLAINTIFF_AVATAR_COLOR : DEFENDANT_AVATAR_COLOR;
  const avatarLabel = isPlaintiff ? 'plaintiff' : 'defendant';

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg border"
      style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgCard }}
      data-testid={`party-card-${party.id}`}
    >
      <Avatar letter={avatarLetter} color={avatarColor} ariaLabel={avatarLabel} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium truncate" style={{ color: brand.colors.text }}>
            {displayName}
          </span>
          {party.partyType === 'organization' && (
            <span className="text-xs" style={{ color: brand.colors.textLight }}>
              (Org)
            </span>
          )}
          {party.isLeadClient && (
            <span
              data-testid="lead-client-pill"
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{
                backgroundColor: `${brand.colors.primary}15`,
                color: brand.colors.link,
              }}
            >
              your client
            </span>
          )}
          {/* Court-reported waiver (SOR-239) — read-only observation beside
              the filer's own flag, never a second control for it. Presence-
              gated: a court-silent party renders nothing here. */}
          {party.courtWaiver && (
            <span
              data-testid="court-waiver-pill"
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{
                backgroundColor: `${brand.colors.warning || '#F59E0B'}15`,
                color: brand.colors.warning || '#F59E0B',
              }}
            >
              fee waiver reported by court
            </span>
          )}
        </div>
        <div className="text-xs" style={{ color: brand.colors.textLight }}>
          {party.roleName}
        </div>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={onEdit}
          className="text-xs underline"
          style={{ color: brand.colors.link }}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-xs underline text-red-500"
        >
          Delete
        </button>
      </div>
    </div>
  );
};

interface AttorneyCardProps {
  brand: BrandConfig;
  attorney: EFilingAttorney;
  onEdit: () => void;
  onDelete: () => void;
}

export const AttorneyCard: React.FC<AttorneyCardProps> = ({
  brand,
  attorney,
  onEdit,
  onDelete,
}) => {
  const attorneyName = [attorney.firstName, attorney.lastName].filter(Boolean).join(' ');
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-lg border"
      style={{ borderColor: brand.colors.border, backgroundColor: brand.colors.bgCard }}
      data-testid={`attorney-card-${attorney.id}`}
    >
      <Avatar letter="⚖" color={ATTORNEY_AVATAR_COLOR} ariaLabel="attorney" />
      <div className="flex-1 min-w-0">
        <div className="font-medium truncate" style={{ color: brand.colors.text }}>
          {attorneyName}
        </div>
        <div className="text-xs flex flex-wrap gap-x-3" style={{ color: brand.colors.textLight }}>
          <span className="font-mono">Bar #{attorney.barNumber}</span>
          {attorney.firmName && <span>{attorney.firmName}</span>}
        </div>
      </div>
      <div className="flex gap-2 flex-shrink-0">
        <button
          type="button"
          onClick={onEdit}
          className="text-xs underline"
          style={{ color: brand.colors.link }}
        >
          Edit
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-xs underline text-red-500"
        >
          Delete
        </button>
      </div>
    </div>
  );
};

interface PartyCardsProps {
  brand: BrandConfig;
  parties: EFilingParty[];
  attorneys: EFilingAttorney[];
  getPartyDisplayName: (party: EFilingParty) => string;
  onEditParty: (party: EFilingParty) => void;
  onDeleteParty: (partyId: string) => void;
  onEditAttorney: (attorney: EFilingAttorney) => void;
  onDeleteAttorney: (attorneyId: string) => void;
  onAddParty: () => void;
  onAddAttorney: () => void;
}

const PartyCards: React.FC<PartyCardsProps> = ({
  brand,
  parties,
  attorneys,
  getPartyDisplayName,
  onEditParty,
  onDeleteParty,
  onEditAttorney,
  onDeleteAttorney,
  onAddParty,
  onAddAttorney,
}) => {
  return (
    <div className="space-y-3">
      {parties.map(party => (
        <PartyCard
          key={party.id}
          brand={brand}
          party={party}
          displayName={getPartyDisplayName(party)}
          onEdit={() => onEditParty(party)}
          onDelete={() => onDeleteParty(party.id)}
        />
      ))}
      {attorneys.map(attorney => (
        <AttorneyCard
          key={attorney.id}
          brand={brand}
          attorney={attorney}
          onEdit={() => onEditAttorney(attorney)}
          onDelete={() => onDeleteAttorney(attorney.id)}
        />
      ))}
      <div
        className="flex items-center gap-3 p-3 rounded-lg border-2 border-dashed"
        style={{ borderColor: brand.colors.border }}
      >
        <span className="text-sm" style={{ color: brand.colors.textLight }}>
          Add a party or attorney
        </span>
        <div className="flex gap-2 ml-auto">
          <button
            type="button"
            onClick={onAddParty}
            className="px-3 py-1.5 rounded-lg text-xs font-medium"
            style={{ backgroundColor: brand.colors.primary, color: brand.colors.onPrimary }}
          >
            Add Party
          </button>
          <button
            type="button"
            onClick={onAddAttorney}
            className="px-3 py-1.5 rounded-lg text-xs font-medium border"
            style={{ borderColor: brand.colors.primary, color: brand.colors.link }}
          >
            Add Attorney
          </button>
        </div>
      </div>
    </div>
  );
};

export default PartyCards;
